import dayjs from "dayjs";
import customFetch from "./api";
import { Calendar2 } from "components/shared/icons";

let lng = localStorage.getItem("default-lg") || "ar";

/**
 * Normalize custom field value from URL (e.g. "1,402") to API format (e.g. [1, 402]).
 * URL search params are always strings; multi-select values come as comma-separated.
 */

const normalizeCustomFieldValue = (value) => {
  if (value == null || value === "") return value;
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((v) => (v.trim() === "" ? NaN : parseInt(v.trim(), 10)))
      .filter((n) => !Number.isNaN(n));
  }
  const num = parseInt(value, 10);
  return Number.isNaN(num) ? value : num;
};

export const getSearchCustomFields = (data) => {
  const customFields = [];
  let updatedSearchParams = {};

  for (const key in data) {
    if (key.startsWith("custom_fields[")) {
      const id = key.match(/\[([0-9]+)\]/)[1];
      const rawValue = data[key];

      if (rawValue !== "0") {
        const value = normalizeCustomFieldValue(rawValue);
        customFields.push({
          id: parseInt(id, 10),
          value: value,
        });
      }
    } else {
      updatedSearchParams[key] = data[key];
    }
  }

  return [customFields, updatedSearchParams];
};
export const normalizedDataToFormData = (formData, customFieldsData) => {
  const allFields = Object.values(customFieldsData).flatMap(
    (category) => category.fields
  );
  const normalize = (value) =>
    value === null || value === undefined ? "" : value;

  // Build one merged entry per field so content + en_content always travel together.
  return allFields.map((field) => {
    const arKey = String(field.id);
    const enKey = `en_${field.id}`;
    const arValue = normalize(formData[arKey]);
    const enValue = normalize(formData[enKey]);

    if (field.type === "select") {
      return {
        content: "",
        en_content: enValue,
        custom_field_id: arKey,
        option_id: arValue,
      };
    }
    if (field.type === "bool" || field.type === "checkbox") {
      return {
        content: arValue === true || String(arValue) === "1" ? "1" : "",
        en_content: enValue,
        custom_field_id: arKey,
        option_id: "",
      };
    }
    return {
      content: arValue,
      en_content: enValue,
      custom_field_id: arKey,
      option_id: null,
    };
  });
};

/** Map API `custom_fields_data` items to the `custom_fields` payload shape used in forms. */
export const normalizeCustomFieldsFromApiData = (custom_fields_data) =>
  (custom_fields_data ?? []).map((item) => {
    const type = item.custom_field?.type;
    const fieldId = String(item.custom_field?.id);
    const enContent = item.en_content ?? "";
    if (type === "select") {
      return {
        content: "",
        en_content: enContent,
        custom_field_id: fieldId,
        option_id: item.custom_field_option_id,
      };
    }
    return {
      content: item.content ?? "",
      en_content: enContent,
      custom_field_id: fieldId,
      option_id: null,
    };
  });

export const normalizedDataOfPrefrencesToFormData = (
  formData,
  customFieldsData
) => {
  const result = [];
  const allFields = Object.values(customFieldsData).flatMap(
    (category) => category.fields
  );

  Object.entries(formData).forEach(([key, value]) => {
    const fieldData = allFields.find(({ id }) => String(id) === String(key));
    let normalized = value == null || value === undefined ? "" : value;
    if (fieldData && normalized) {
      if (fieldData.type === "bool") {
        normalized = 1;
      }
      if (
        fieldData.search_type === "range" &&
        Array.isArray(normalized) &&
        normalized[0] === fieldData.min_value &&
        normalized[1] === fieldData.min_value
      ) {
        return; // skip this field
      }
      result.push({
        id: key,
        value: normalized,
      });
    }
  });
  return result;
};
export const normalizedDataFromCustomFields = (data) => {
  return data.map((d) => ({
    icon: d.custom_field.icon,
    title: d.custom_field.title,
    value:
      d.custom_field.type === "select"
        ? d.custom_field_option.value
        : d.content,
    type: d.custom_field.type,
  }));
};

export const hasUnits = (data) => {
  return data?.has_units === 1;
};

export const toQueryString = (params) => {
  return Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
};

export const getDateOnly = (DATE, onlyYear = false) => {
  const date = new Date(DATE);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const formattedDate = onlyYear ? year : `${year}-${month}-${day}`;
  return formattedDate;
};

export function translate(title, en_title) {
  return LANGUAGE === "ar" ? title : en_title;
}

export function translateWithKey(key, data) {
  return LANGUAGE === "ar" ? data?.[key] : data?.[`en_${key}`];
}

export function checkText(value, defaultValue) {
  if (!value || value === "null" || value == null) {
    return defaultValue;
  }
  return value;
}
export const getFileIconSrc = (file) => {
  const extension = file?.path.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return "/pdf-file.svg";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return file?.url;
    case "doc":
    case "docx":
      return "/pdf-file.svg";
    default:
      return "/pdf-file.svg"; // Default to PDF if the file type is unknown
  }
};

// export const transDataToTree = (data, codes) => {
//   const idMap = new Map();

//   // First pass: create a map of all nodes

//   data?.forEach((node) => {
//     idMap.set(node.id, {
//       value: node.id,
//       label: translate(node?.name, node?.en_name),
//       code: node.code,
//       parent_id: node.parent_id,
//       children: [],
//       en_label: node.en_name,
//       agent_id: node.agent_id,
//       cash_flow_type: node.cash_flow_type,
//     });
//   });

//   const treeData = [];

//   // Second pass: build the tree
//   data?.forEach((node) => {
//     const currentNode = idMap.get(node.id);

//     const condition = codes
//       ? codes.includes(node.code)
//       : node.parent_id === null;

//     if (condition) {
//       treeData.push(currentNode);
//     } else {
//       const parent = idMap.get(node?.parent_id);

//       if (parent) {
//         parent.children.push(currentNode);
//       } else {
//         console.warn(`Parent not found for node: ${node.id}`);
//       }
//     }
//   });

//   return treeData;
// };

/**
 * Cascader options: `disabled` only on non-postable leaves so parents stay expandable.
 *
 * `orphansAsRoots` opts into rendering a node whose `parent_id` names a row that is not
 * in `data` at all. By default such a node is dropped (with a console warning), which is
 * right for a picker fed the whole tree — a missing parent there means bad data. It is
 * wrong for any view that is deliberately handed a *subset*: the deleted-accounts view
 * lists only trashed rows, and since an account cannot be deleted while it still has
 * children, every row in it is a leaf whose parent is alive and therefore absent. Left to
 * the default, that view renders empty no matter how many rows the endpoint returned.
 *
 * Ignored when `codes` is given: that mode scopes the output to named sub-tree roots, so
 * promoting an orphan would surface a node from outside the requested scope.
 */
export const transDataToTree = (
  data,
  codes,
  { requireHead = false, orphansAsRoots = false } = {}
) => {
  // Handle null, undefined, or empty data
  if (!data) {
    return [];
  }

  // Handle single object - wrap in array
  const dataArray = Array.isArray(data) ? data : [data];

  if (dataArray.length === 0) {
    return [];
  }

  // Flatten nested data structure if it has children
  const flattenNodes = (nodes, flatArray = []) => {
    nodes.forEach((node) => {
      // Extract node data without children
      const { children, ...nodeData } = node;
      flatArray.push(nodeData);

      // Recursively flatten children if they exist
      if (children && Array.isArray(children) && children.length > 0) {
        flattenNodes(children, flatArray);
      }
    });
    return flatArray;
  };

  // Check if data is nested (has children arrays)
  const isNested = dataArray.some(
    (node) =>
      node.children && Array.isArray(node.children) && node.children.length > 0
  );

  // Flatten if nested, otherwise use data as-is
  const flatData = isNested ? flattenNodes(dataArray) : dataArray;

  const parentIdsWithChildren = new Set();
  flatData.forEach((node) => {
    if (node.parent_id != null) {
      parentIdsWithChildren.add(node.parent_id);
    }
  });

  const idMap = new Map();

  // First pass: create a map of all nodes
  flatData.forEach((node) => {
    const hasChildrenInTree = parentIdsWithChildren.has(node.id);
    idMap.set(node.id, {
      value: node.id,
      label: translate(node?.name, node?.en_name),
      // `code` is the chart template's key the `codes` scoping matches; people are shown user_code.
      code: node.code,
      user_code: node.user_code,
      parent_id: node.parent_id,
      children: [],
      en_label: node.en_name,
      agent_id: node.agent_id,
      cash_flow_type: node.cash_flow_type,
      report_section: node.report_section,
      is_head: Boolean(node.is_head),
      disabled: requireHead
        ? !node.is_head
        : node.is_postable === false && !hasChildrenInTree,
    });
  });

  const treeData = [];

  // Second pass: build the tree
  flatData.forEach((node) => {
    const currentNode = idMap.get(node.id);

    if (!currentNode) {
      return; // Skip if node not found in map
    }

    const condition = codes
      ? codes.includes(node.code)
      : node.parent_id === null;

    if (condition) {
      treeData.push(currentNode);
    } else {
      const parent = idMap.get(node?.parent_id);

      if (parent) {
        parent.children.push(currentNode);
      } else if (orphansAsRoots && !codes) {
        treeData.push(currentNode);
      } else {
      }
    }
  });

  return treeData;
};
export const direction = () => {
  const lng = localStorage.getItem("default-lg") || "ar";
  return lng === "ar" ? "rtl" : "ltr";
};

const currency = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;

    const user = JSON.parse(raw);
    const currency = user?.agent?.currency;

    if (!currency) {
      return user?.agent?.currency_code;
    }

    if (currency.icon_url) {
      return (
        <img
          src={currency.icon_url}
          alt={currency.code}
          className="inline-block w-[1.17em] h-[1.17em] ms-1 align-middle"
        />
      );
    }

    return currency.code ?? null;
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
    return null;
  }
};

/**
 * The tenant's currency icon URL, dark-mode aware. Falls back to the light
 * icon when no `dark_mode_icon_url` is configured, and to `null` when the
 * currency has no icon at all — render `CURRENCY_TEXT` in that case.
 */
export const getCurrencyIconUrl = (isDark = false) => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;

    const currency = JSON.parse(raw)?.agent?.currency;
    if (!currency) return null;

    return isDark
      ? currency.dark_mode_icon_url ?? currency.icon_url ?? null
      : currency.icon_url ?? null;
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
    return null;
  }
};

/** The tenant's currency code as a plain string, or null when unknown. */
const currencyText = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");

    return user?.agent?.currency?.code ?? user?.agent?.currency_code ?? null;
  } catch (e) {
    return null;
  }
};

/**
 * The tenant's currency, ready to render.
 *
 * WARNING: this is a React node, not always a string. Currencies configured
 * with an `icon_url` (the riyal glyph, for one) come back as an <img>; the rest
 * come back as their code. Interpolating it into a template literal therefore
 * prints "[object Object]" — and only on the tenants that have an icon, so it
 * looks perfectly fine in development and breaks in production.
 *
 *   OK:  <FormattedAmount value={total} suffix={CURRENCY} />
 *   OK:  <span>{amount} {CURRENCY}</span>
 *   NO:  `${amount} ${CURRENCY}`   ->  "1,000.00 [object Object]"
 *
 * When a real string is unavoidable — a chart tooltip, a `title` attribute,
 * exported text — use CURRENCY_TEXT instead.
 */
export const CURRENCY = currency();

/**
 * The tenant's currency code as a plain string (e.g. "SAR"), for the places a
 * React node cannot go. Prefer CURRENCY anywhere that renders, so icon-based
 * currencies keep their glyph.
 */
export const CURRENCY_TEXT = currencyText();

/**
 * Active interface language ("ar" | "en"). Updated at runtime via `setLanguage()`.
 * Always read inside functions — do NOT cache at module scope.
 * @example
 *   // ✅ correct — read at call time
 *   const url = `/${LANGUAGE}/customers`;
 *
 *   // ❌ wrong — caches the initial value, won't update after a language switch
 *   const MY_URL = `/${LANGUAGE}/customers`;
 */
export let LANGUAGE = lng;

export function setLanguage(lang) {
  lng = lang;
  LANGUAGE = lang;
}

// Static snapshot at app load. Direction is managed via document.dir in App.js.
export const DIRECTION = direction();

export const findPathById = (treeData, id) => {
  if (!treeData || !id) {
    return [];
  }

  // Handle single object - wrap in array
  const dataArray = Array.isArray(treeData) ? treeData : [treeData];

  // Flatten nested structure to get all nodes
  const flattenNodes = (nodes, flatMap = new Map()) => {
    nodes.forEach((node) => {
      flatMap.set(node.id, node);
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        flattenNodes(node.children, flatMap);
      }
    });
    return flatMap;
  };

  const allNodes = flattenNodes(dataArray);
  const parentIds = [];
  const targetNode = allNodes.get(id);

  if (!targetNode) {
    return [];
  }

  // Build path by traversing parent_id chain
  let currentNode = targetNode;
  while (currentNode) {
    parentIds.push(currentNode.id);
    if (currentNode.parent_id !== null && currentNode.parent_id !== undefined) {
      currentNode = allNodes.get(currentNode.parent_id);
    } else {
      break;
    }
  }

  return parentIds.reverse();
};

// `tillCode` may be a list, for a field scoped to several roots.
export const updatedFindPathById = (treeData = [], id, tillCode) => {
  if (!treeData || !id) {
    return [];
  }
  const tillCodes = [].concat(tillCode);

  // Handle single object - wrap in array
  const dataArray = Array.isArray(treeData) ? treeData : [treeData];

  // Flatten nested structure to get all nodes
  const flattenNodes = (nodes, flatMap = new Map()) => {
    nodes.forEach((node) => {
      flatMap.set(node.id, node);
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        flattenNodes(node.children, flatMap);
      }
    });
    return flatMap;
  };

  const allNodes = flattenNodes(dataArray);
  const parentIds = [];
  const targetNode = allNodes.get(id);

  if (!targetNode) {
    return [];
  }

  // Build path by traversing parent_id chain, stopping at tillCode
  let currentNode = targetNode;
  while (currentNode) {
    parentIds.push(currentNode.id);
    if (
      currentNode.parent_id !== null &&
      currentNode.parent_id !== undefined &&
      !tillCodes.includes(currentNode.code)
    ) {
      currentNode = allNodes.get(currentNode.parent_id);
    } else {
      break;
    }
  }

  return parentIds.reverse();
};

export const findPathByIdWithCode = (treeData, id, code) => {
  if (!treeData || !id) {
    return [];
  }

  // Handle single object - wrap in array
  const dataArray = Array.isArray(treeData) ? treeData : [treeData];

  // Flatten nested structure to get all nodes
  const flattenNodes = (nodes, flatMap = new Map()) => {
    nodes.forEach((node) => {
      flatMap.set(node.id, node);
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        flattenNodes(node.children, flatMap);
      }
    });
    return flatMap;
  };

  const allNodes = flattenNodes(dataArray);
  const parentIds = [];
  const targetNode = allNodes.get(id);

  if (!targetNode) {
    return [];
  }

  // Build path by traversing parent_id chain, stopping when code matches
  let currentNode = targetNode;
  while (currentNode) {
    parentIds.push(currentNode.id);
    if (
      currentNode.parent_id !== null &&
      currentNode.parent_id !== undefined &&
      code !== currentNode.code
    ) {
      currentNode = allNodes.get(currentNode.parent_id);
    } else {
      break;
    }
  }

  return parentIds.reverse();
};

export const findPath = (tree, targetId) => {
  if (!tree || !targetId) {
    return [];
  }

  // Handle single object - wrap in array
  const dataArray = Array.isArray(tree) ? tree : [tree];

  // Recursive function to search through nested children
  const searchPath = (nodes, targetId, currentPath = []) => {
    for (const node of nodes) {
      const newPath = [...currentPath, node.id];

      if (node.id === targetId) {
        return newPath;
      }

      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        const childPath = searchPath(node.children, targetId, newPath);
        if (childPath.length) {
          return childPath;
        }
      }
    }
    return [];
  };

  return searchPath(dataArray, targetId);
};

export const disableNode = (options = [], targetId) => {
  if (!Array.isArray(options)) return [];

  return options.map((option) => {
    const disabled = option.id === targetId;
    return {
      ...option,
      value: option.value, // Cascader uses value
      label: option.label, // Cascader uses label
      disabled,
      children: option.children?.length
        ? disableNode(option.children, targetId)
        : [],
    };
  });
};

export const getFormattedDateTime = (date, active) => {
  if (!date) return "";

  // ✅ Detect and fix "DD-MM-YYYY HH:mm:ss" format manually
  let formattedDateStr = date;
  if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
    const [day, month, yearAndTime] = date.split("-");
    const [year, time] = yearAndTime.split(" ");
    formattedDateStr = `${year}-${month}-${day} ${time || ""}`; // -> "YYYY-MM-DD HH:mm:ss"
  }

  const now = new Date(formattedDateStr);
  const isArabic = LANGUAGE === "ar";

  const arabicOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    numberingSystem: "arab",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const englishOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const formattedDate = now.toLocaleDateString(
    isArabic ? "ar-EG" : LANGUAGE,
    isArabic
      ? arabicOptions
      : { ...englishOptions, hour: undefined, minute: undefined }
  );

  const formattedTime = now.toLocaleTimeString(
    isArabic ? "ar-EG" : LANGUAGE,
    isArabic ? arabicOptions : englishOptions
  );

  return (
    <div
      className={`flex justify-center gap-1.5 border rounded-[4px] w-fit px-1.5 py-1 ${active ? "border-primary" : "border-ink-body"
        }`}
    >
      <Calendar2 active={active} />
      <span
        className={`text-[13px] font-normal ${active ? "text-primary" : "text-ink-body"
          }`}
      >
        {!isArabic ? formattedTime : formattedDate}
      </span>
    </div>
  );
};

export const getFormattedDateTime2 = (date, hasTime = true) => {
  const now = new Date(date);

  let formattedDate;
  let formattedTime;

  if (LANGUAGE === "ar") {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    formattedDate = now.toLocaleDateString("ar-EG-u-nu-arab", options);
    formattedTime = now.toLocaleTimeString("ar-EG-u-nu-arab", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate}  ${hasTime ? "-" + formattedTime : ""}`;
  } else {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    formattedDate = now.toLocaleDateString(LANGUAGE, options);
    formattedTime = now.toLocaleTimeString(LANGUAGE, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate}  ${hasTime ? "-" + formattedTime : ""}`;
  }
};

export const getTime = (dateString) => {
  const date = new Date(dateString);

  let hours = date.getHours();
  const minutes = date.getMinutes();

  if (LANGUAGE === "en") {
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  } else if (LANGUAGE === "ar") {
    const period = hours >= 12 ? "مساء" : "صباحا";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  return dateString;
};

export const convertToArabicNumerals = (number) => {
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  const num =
    LANGUAGE === "en"
      ? number
      : number
        .toString()
        .split("")
        .map((digit) => (/\d/.test(digit) ? arabicNumerals[digit] : digit))
        .join("");
  return num + " ";
};

export const dueStats = (invoice_id, issued_at) => {
  let value = {};
  const now = new Date();

  if (invoice_id !== null) {
    value = { title: "تم المعالجة", en_title: "collected" };
  } else {
    const DUE_DATE = new Date(issued_at);

    if (DUE_DATE.getTime() < now.getTime()) {
      value = { title: "متأخرة", en_title: "delayed" };
    } else {
      value = { title: "مجدوله", en_title: "scheduled" };
    }
  }
  return translate(value.title, value.en_title);
};

export const sinceWhen = (timestamp) => {
  const createdDate = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - createdDate) / 1000);

  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) {
    return interval === 1
      ? translate(`منذ ${interval} عام`, `${interval} year ago`)
      : translate(`منذ ${interval} أعوام`, `${interval} years ago`);
  }

  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) {
    return interval === 1
      ? translate(`منذ ${interval} شهر`, `${interval} month ago`)
      : translate(`منذ ${interval} أشهر`, `${interval} months ago`);
  }

  interval = Math.floor(seconds / 86400);
  if (interval >= 1) {
    return interval === 1
      ? translate(`منذ ${interval} يوم`, `${interval} day ago`)
      : translate(`منذ ${interval} أيام`, `${interval} days ago`);
  }

  interval = Math.floor(seconds / 3600);
  if (interval >= 1) {
    return interval === 1
      ? translate(`منذ ${interval} ساعة`, `${interval} hour ago`)
      : translate(`منذ ${interval} ساعات`, `${interval} hours ago`);
  }

  interval = Math.floor(seconds / 60);
  if (interval >= 1) {
    return interval === 1
      ? translate(`منذ ${interval} دقيقة`, `${interval} minute ago`)
      : translate(`منذ ${interval} دقائق`, `${interval} minutes ago`);
  }

  return seconds === 1
    ? translate(`منذ ${seconds} ثانية`, `${seconds} second ago`)
    : translate(`منذ ${seconds} ثواني`, `${seconds} seconds ago`);
};

export function formatTimeWithArabicPeriod(datetimeStr) {
  const date = dayjs(datetimeStr);
  const time = date.format("hh:mm");
  const isPM = date.hour() >= 12;

  let suffix;
  if (LANGUAGE === "ar") {
    suffix = isPM ? "مساءً" : "صباحًا";
  } else {
    suffix = isPM ? "PM" : "AM";
  }

  return `${time} ${suffix}`;
}

export const normalizeValue = (value) =>
  value === null || value === undefined ? "" : value;

export const normalizeNumberValue = (value) =>
  value === null || value === undefined ? 0 : Number(value);

export const validatDateAfter = (value, t) => {
  if (!value) {
    return Promise.resolve();
  }
  const picked = dayjs(value).startOf("day");
  const today = dayjs().startOf("day");

  if (picked.isSame(today) || picked.isBefore(today)) {
    return Promise.reject(
      new Error(t("formValidationMessages.dateAfterToday"))
    );
  }
  return Promise.resolve();
};

export const validatDateBefore = (value, t) => {
  if (!value) {
    return Promise.resolve();
  }
  const picked = dayjs(value).startOf("day");
  const today = dayjs().startOf("day");

  if (picked.isSame(today) || picked.isAfter(today)) {
    return Promise.reject(
      new Error(t("formValidationMessages.dateBeforeToday"))
    );
  }
  return Promise.resolve();
};

export const validateDateNotInFuture = (value, t) => {
  if (!value) {
    return Promise.resolve();
  }
  const picked = dayjs(value).startOf("day");
  const today = dayjs().startOf("day");

  if (picked.isAfter(today)) {
    return Promise.reject(
      new Error(t("formValidationMessages.dateNotInFuture"))
    );
  }
  return Promise.resolve();
};

export const validateMinAge22 = (value, t) => {
  if (!value) {
    return Promise.resolve();
  }

  const picked = dayjs(value).startOf("day");
  const minAllowed = dayjs().startOf("day").subtract(22, "year");

  if (picked.isAfter(minAllowed)) {
    return Promise.reject(new Error(t("formValidationMessages.minAge22")));
  }

  return Promise.resolve();
};

export const PhoneLtr = ({ phone }) => {
  return (
    <span style={{ direction: "ltr", unicodeBidi: "bidi-override" }}>
      {phone}
    </span>
  );
};

export const validateNumberDigitLength = (value, min, max, t) => {
  if (value === null || value === undefined) return Promise.resolve();
  const length = String(value).length;
  if (length < min || length > max) {
    return Promise.reject(
      new Error(t("formItemRules.lengthMessage", { min, max }))
    );
  }
  return Promise.resolve();
};

export const validateNumberRange = (value, min, max, t) => {
  if (value === null || value === undefined || value === "") {
    return Promise.resolve();
  }

  const number = Number(value);

  if (isNaN(number)) {
    return Promise.reject(new Error(t("formItemRules.numbersOnly")));
  }

  if (number < min) {
    return Promise.reject(new Error(t("formItemRules.minLength", { min })));
  }

  if (number > max) {
    return Promise.reject(new Error(t("formItemRules.maxValue", { max })));
  }

  return Promise.resolve();
};

// discount_type: "percentage" | "fixed"
// discount_value: number is discount value if discount_type is "fixed"
// discount: number is percentage value
export const calculateRowTotal = async (
  price,
  amount,
  taxes_ids,
  discount,
  discount_type = "percentage",
  discount_value = 0
) => {
  try {
    if (!price || !amount) {
      return {
        base_amount: 0,
        taxes: 0,
        total_amount: 0,
        discount_amount: 0,
      };
    }

    const response = await customFetch(`dashboard/taxes/calculate`, {
      method: "POST",
      body: {
        amount: price,
        quantity: amount,
        discount,
        taxes_ids,
        discount_type,
        discount_value,
      },
    });

    if (response?.data) {
      // assuming your API returns { total: number, taxes: number }
      return {
        base_amount: response.data.base_amount || 0,
        taxes: response.data.tax_amount || 0,
        total_amount: response.data.total_amount || 0,
        discount_amount: response.data.discount_amount || 0,
        initial_amount: response.data.initial_amount || 0,
      };
    }
  } catch (error) {
    console.error("Failed to calculate taxes:", error);
  }
  return {
    base_amount: 0,
    taxes: 0,
    total_amount: 0,
    discount_amount: 0,
  };
};

export const processCustomFields = (searchParam) => {
  const processedSearchParam = { ...searchParam };
  Object.keys(processedSearchParam).forEach((key) => {
    if (key.startsWith("custom_fields[")) {
      const value = processedSearchParam[key];
      if (typeof value === "string") {
        if (value.includes(",")) {
          // Split by comma and convert each to number
          processedSearchParam[key] = value
            .split(",")
            .map((v) => Number(v.trim()))
            .filter((v) => !isNaN(v));
        } else {
          // Single number string, convert to number
          const numValue = Number(value);
          processedSearchParam[key] = !isNaN(numValue) ? numValue : value;
        }
      }
    }
  });
  return processedSearchParam;
};
export const getDueStatus = (collected, issued_at) => {
  let value = {};
  const now = new Date();

  if (collected) {
    value = { title: "تم المعالجة", en_title: "collected" };
  } else {
    const DUE_DATE = new Date(issued_at);

    if (DUE_DATE.getTime() < now.getTime()) {
      value = { title: "متأخرة", en_title: "delayed" };
    } else {
      value = { title: "مجدوله", en_title: "scheduled" };
    }
  }
  return translate(value.title, value.en_title);
};

export const getDueStatusValue = (collected, issued_at) => {
  let value = {};
  const now = new Date();

  if (collected) {
    value = "collected";
  } else {
    const DUE_DATE = new Date(issued_at);

    if (DUE_DATE.getTime() < now.getTime()) {
      value = "delayed";
    } else {
      value = "scheduled";
    }
  }
  return value;
};

export const formatNumberTo2Decimals = (number) => {
  return `${Number(number).toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export { sub, addDays, addHours, subDays, subHours } from "utils/date";
